"use client";

import OprDialog from "@/components/_shared/controls/OprDialog";
import { CmsContentItem, CmsContentItemTypes, CmsSlotType } from "@/data/types";
import { CmsImage } from "@/db/cmsImagesRepository";
import useCmsImageItems from "@/hooks/cms/useCmsImageItems";
import useCmsMarkdownItems from "@/hooks/cms/useCmsMarkdownItems";
import { getCdnUrl } from "@/services";
import { Add } from "@mui/icons-material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { Button, IconButton, Stack, Tab, Tabs, Typography } from "@mui/material";
import { useState } from "react";
import CmsImageEditorView from "../images/CmsImageEditorView";
import CmsItemMarkdownEditorView from "../markdown/CmsItemMarkdownEditorView";

type Props = {
  slotType: CmsSlotType;
  items: CmsContentItem[];
  setSlotContent: (content: CmsContentItem[]) => void;
};

export default function CmsContentListEditor({ slotType, items, setSlotContent }: Props) {
  const { data: imageItems = [] } = useCmsImageItems();
  const { data: markdownItems = [] } = useCmsMarkdownItems();

  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingItemId, setEditingItemId] = useState<string | null>(null);
  const editingImage = imageItems.find((img) => img.id === editingItemId) || null;
  const editingMarkdown = markdownItems.find((md) => md.id === editingItemId) || null;
  const editingContent = items.find((x) => x.id === editingItemId) || null;
  const editingItemParameters = editingContent?.parameters;
  const [tab, setTab] = useState(0);

  const handleAddItem = (itemId: string, type: CmsContentItemTypes) => {
    const newItem: CmsContentItem = {
      id: itemId,
      type
    };
    const updatedContent = [...items, newItem];
    setSlotContent(updatedContent);
    setShowAddDialog(false);
  };

  const setContentItem = (item: CmsContentItem) => {
    if (!item?.id) return;
    setSlotContent(items.map((x) => (x.id === item.id ? item : x)));
  };

  const handleSelectItem = (slotItem: CmsContentItem) => {
    setEditingItemId(slotItem.id);
    if (slotItem.type === "CmsImage") {
      setTab(0);
    } else {
      setTab(1);
    }
  };

  const handleRemoveItem = (index: number) => {
    const updatedContent = items.filter((_, i) => i !== index);
    setSlotContent(updatedContent);
  };

  const handleMoveItem = (index: number, direction: "up" | "down") => {
    const newIndex = direction === "up" ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= items.length) return;

    const newItems = [...items];
    [newItems[index], newItems[newIndex]] = [newItems[newIndex], newItems[index]];

    setSlotContent(newItems);
  };

  const handleItemSaved = (savedItem: { id: string }, type: CmsContentItemTypes) => {
    if (!items.some((item) => item.id === savedItem.id)) {
      handleAddItem(savedItem.id, type);
    }

    setEditingItemId(null);
    setShowAddDialog(false);
  };

  const handleItemDeleted = (deletedItemId: string) => {
    // Find index of the item with this image ID and remove it
    const index = items.findIndex((item) => item.id === deletedItemId);
    if (index !== -1) {
      handleRemoveItem(index);
    }
  };

  return (
    <>
      <Stack sx={{ p: 2, mb: 2 }}>
        <Stack
          direction="row"
          sx={{
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2
          }}
        >
          <Typography variant="subtitle1">Content Items</Typography>
          <Button variant="outlined" size="small" onClick={() => setShowAddDialog(true)}>
            <Add sx={{ mr: 1 }} /> Add Item
          </Button>
        </Stack>

        {items.length === 0 ? (
          <Typography variant="body2" color="textSecondary">
            No content items added yet
          </Typography>
        ) : (
          items.map((slotItem, index) => {
            const item = (() => {
              if (slotItem.type === CmsContentItemTypes.CmsImage) {
                return imageItems.find((img) => img.id === slotItem.id) || null;
              } else if (slotItem.type === CmsContentItemTypes.CmsMarkdown) {
                return markdownItems.find((md) => md.id === slotItem.id) || null;
              }
              return null;
            })();

            const imageItem = item as CmsImage;

            return (
              <Stack
                key={index}
                direction="row"
                spacing={2}
                sx={{
                  alignItems: "center",
                  p: 1,
                  border: "1px solid var(--mui-palette-divider)",
                  borderRadius: 1,
                  mb: 1
                }}
              >
                {slotItem.type === CmsContentItemTypes.CmsImage && imageItem?.url && (
                  <img
                    src={getCdnUrl(imageItem.url)}
                    alt={imageItem.key}
                    style={{
                      width: 64,
                      height: 64,
                      objectFit: "cover",
                      borderRadius: 4
                    }}
                  />
                )}
                <Typography variant="body2" sx={{ flex: 1 }}>
                  {item?.key || slotItem.id}
                </Typography>
                <IconButton size="small" color="info" onClick={() => handleSelectItem(slotItem)} title="Edit">
                  <EditIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" onClick={() => handleMoveItem(index, "up")} disabled={index === 0}>
                  <KeyboardArrowUpIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => handleMoveItem(index, "down")}
                  disabled={index === items.length - 1}
                >
                  <KeyboardArrowDownIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" color="error" onClick={() => handleRemoveItem(index)}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Stack>
            );
          })
        )}
      </Stack>
      <OprDialog
        open={showAddDialog || !!editingItemId}
        onClose={() => {
          setEditingItemId(null);
          setShowAddDialog(false);
        }}
        title="Edit CMS Item"
        maxWidth={editingMarkdown ? "lg" : "md"}
        disableContentPadding
      >
        {showAddDialog && (
          <Tabs value={tab} onChange={(_, x) => setTab(x)}>
            <Tab label="Image Item" />
            <Tab label="Markdown Item" />
          </Tabs>
        )}
        {tab === 0 && (
          <CmsImageEditorView
            slotType={slotType}
            selectedItem={editingImage}
            editingItemParameters={editingItemParameters}
            setEditingItemParameters={(x) => setContentItem({ ...editingContent, parameters: x })}
            onSaved={(x) => handleItemSaved(x, CmsContentItemTypes.CmsImage)}
            onDeleted={handleItemDeleted}
          />
        )}
        {tab === 1 && (
          <CmsItemMarkdownEditorView
            selectedItem={editingMarkdown}
            onSaved={(x) => handleItemSaved(x, CmsContentItemTypes.CmsMarkdown)}
            onDeleted={handleItemDeleted}
          />
        )}
        {/* <CmsItemParametersEditor
          type={slotType}
          parameters={editingContent?.parameters ?? {}}
          setItemParameters={(x) => {
            if (!editingContent) return;
            setContentItem({ ...editingContent, parameters: x });
          }}
        /> */}
      </OprDialog>
    </>
  );
}
